/**
 * Chapter 2 — one container on a ruler: slots pop at the minutes the real
 * timing helper computes, amounts type in, an extra slot hits the end wall
 * and is cut, and the Board's drops sentence assembles. Local + Danish
 * times come from the real formatters.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';

import { dkLabel } from '@/features/drop-generator/utils/clock/dk-label';
import { minutesLabel } from '@/features/drop-generator/utils/format';
import { describeDrops } from '@/features/drop-generator/utils/drops/describe-drops';
import { computeContainerTimes, isPlanned, planWindow } from '@/features/drop-generator/utils/slot-times';
import { DEMO_PLAN, DEMO_TZ, WARM_UP } from '../../fixtures';
import { Enter, Node } from '../../motion/Node';
import { Illustration } from '../../motion/Illustration';

const CYCLE = 12;
const RULER_START = 10 * 60;
const RULER_END = 16 * 60;
/** The sheet's WARM UP with an end time and one slot too many — the wall. */
const SHOWN = {
  ...WARM_UP,
  endAtMin: WARM_UP.startAtMin! + 6 * WARM_UP.intervalMin + 20,
  slots: [...WARM_UP.slots, { id: 'extra', amount: null, enabled: true }],
};
// The demo's times are read as written — no Danish clock conversion for a made-up plan.
const TIMES = computeContainerTimes([SHOWN], { ...planWindow(DEMO_PLAN), day: undefined }).get(SHOWN.id)!;
const pct = (minute: number) => `${((minute - RULER_START) / (RULER_END - RULER_START)) * 100}%`;
const hourTicks = Array.from({ length: (RULER_END - RULER_START) / 60 + 1 }, (_, i) => RULER_START + i * 60);

export const ContainerIllustration: FC = () => (
  <Illustration cycleSeconds={CYCLE}>
    <div className="relative mx-8 mt-2 h-[72px] min-w-[560px]">
      <div className="absolute inset-x-0 top-8 h-px bg-border" />
      {hourTicks.map((m) => (
        <span key={m} className="absolute top-10 -translate-x-1/2 font-mono text-[10px] text-muted-foreground" style={{ left: pct(m) }}>
          {minutesLabel(m)}
        </span>
      ))}
      <span className="absolute top-5 h-6 w-px bg-destructive/60" style={{ left: pct(SHOWN.endAtMin!) }} />
      <span className="absolute top-[54px] -translate-x-1/2 whitespace-nowrap text-[10px] text-destructive" style={{ left: pct(SHOWN.endAtMin!) }}>
        end {minutesLabel(SHOWN.endAtMin!)}
      </span>
      {TIMES.slotTimes.map((slot, i) => (
        <span key={slot.slotId} className="absolute top-2 -translate-x-1/2" style={{ left: pct(slot.minute) }}>
          <Enter at={i} className="flex flex-col items-center gap-1">
            <span className={`font-mono text-[10px] ${!isPlanned(slot) ? 'text-destructive' : 'text-muted-foreground'}`}>
              {!isPlanned(slot) ? 'cut' : (SHOWN.slots[i]?.amount ?? SHOWN.defaultAmount).toLocaleString('en-US')}
            </span>
            <span className={`h-3 w-3 rounded-full ${!isPlanned(slot) ? 'border border-dashed border-destructive bg-transparent' : 'bg-primary'}`} />
          </Enter>
        </span>
      ))}
    </div>
    <div className="mt-6 flex flex-wrap items-center gap-2 text-xs">
      <Node at={0}>starts {dkLabel(TIMES.startMin, DEMO_TZ)} 🇩🇰 <span className="text-muted-foreground">· {minutesLabel(TIMES.startMin)} local</span></Node>
      <Node at={2}>every {SHOWN.intervalMin} min</Node>
      <Node at={TIMES.slotTimes.length - 1}>
        last drop {dkLabel(TIMES.lastPlannedMin, DEMO_TZ)} 🇩🇰 <span className="text-muted-foreground">· {minutesLabel(TIMES.lastPlannedMin)} local</span>
      </Node>
      <Node at={TIMES.slotTimes.length + 1} className="border-primary/40 bg-primary/5 font-medium">
        {describeDrops(WARM_UP.slots, WARM_UP.defaultAmount, WARM_UP.intervalMin)}
      </Node>
    </div>
  </Illustration>
);
